import { Routes } from '@angular/router';
import { registrationCompletionGuard } from './guards/registration-completion.guard';
import { onboardingGuard } from './guards/onboarding.guard';
import { authGuard, loginGuard, logoutGuard } from './services/auth/auth.guard';
import { MyTripsDetailStateService } from './services/my-trips-detail/my-trips-detail-state.service';

export const mainLayoutRoutes: Routes = [
  /**
   * ONBOARDING - Standalone page without layout
   */
  {
    path: 'onboarding',
    loadComponent: () => import('./pages/onboarding/onboarding.page').then((m) => m.OnboardingPage),
  },
  /**
   * BOILERPLATE FOR ALTERNATIVE LAYOUT USAGE
   */
  {
    path: 'alternative',
    loadComponent: () =>
      import('./layouts/alternative/alternative-layout.component').then((m) => m.AlternativeLayoutComponent),
    children: [
      {
        path: 'home',
        loadComponent: () => import('./pages/home/home.page').then((m) => m.HomePage),
      },
    ],
  },
  /**
   * MAIN LAYOUT
   */
  {
    path: '',
    loadComponent: () => import('./layouts/main/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      {
        path: 'home',
        loadComponent: () => import('./pages/home/home.page').then((m) => m.HomePage),
        canActivate: [onboardingGuard],
      },
      {
        path: 'offers/:origin',
        loadComponent: () => import('./pages/search-results/search-results.page').then((m) => m.SearchResultsPage),
      },
      {
        path: 'offers/:origin/:destination',
        loadComponent: () => import('./pages/search-results/search-results.page').then((m) => m.SearchResultsPage),
      },
      {
        path: 'offers',
        loadComponent: () => import('./pages/search-results/search-results.page').then((m) => m.SearchResultsPage),
      },
      {
        path: 'styleguide',
        loadComponent: () => import('./pages/styleguide/styleguide.page').then((m) => m.StyleguidePage),
      },
      {
        path: 'login',
        loadComponent: () => import('./pages/login/login.page').then((m) => m.LoginPage),
        canActivate: [loginGuard],
      },
      {
        path: 'my-alerts/new',
        loadComponent: () =>
          import('./pages/route-alert/new-alert/new-alert-launcher.page').then((m) => m.NewAlertLauncherPage),
        canActivate: [authGuard],
      },
      {
        path: 'my-alerts',
        loadComponent: () =>
          import('./pages/route-alert/route-alert-launcher.page').then((m) => m.RouteAlertLauncherPage),
        canActivate: [authGuard],
      },
      {
        path: 'my-trips',
        loadComponent: () => import('./pages/my-trips/my-trips.page').then((m) => m.MyTripsPage),
        canActivate: [authGuard],
      },
      {
        path: 'my-trips/:id',
        loadComponent: () =>
          import('./pages/my-trips-detail-shell/my-trips-detail-shell.component').then(
            (m) => m.MyTripsDetailShellComponent
          ),
        providers: [MyTripsDetailStateService],
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/my-trips-detail/my-trips-detail.page').then((m) => m.MyTripsDetailPage),
          },
          {
            path: 'booking-details',
            loadComponent: () =>
              import('./pages/my-trips-booking-details/my-trips-booking-details.page').then(
                (m) => m.MyTripsBookingDetailsPage
              ),
          },
          {
            path: 'vehicle-information',
            loadComponent: () =>
              import('./pages/my-trips-vehicle-information/my-trips-vehicle-information.page').then(
                (m) => m.MyTripsVehicleInformationPage
              ),
          },
          {
            path: 'pickup',
            loadComponent: () =>
              import('./pages/my-trips-pickup/my-trips-pickup.page').then((m) => m.MyTripsPickupPage),
          },
          {
            path: 'dropoff',
            loadComponent: () =>
              import('./pages/my-trips-dropoff/my-trips-dropoff.page').then((m) => m.MyTripsDropoffPage),
          },
          {
            path: 'provider',
            loadComponent: () =>
              import('./pages/my-trips-provider/my-trips-provider.page').then((m) => m.MyTripsProviderPage),
          },
        ],
      },
      {
        path: 'profile',
        loadComponent: () => import('./pages/profile/profile.page').then((m) => m.ProfilePage),
        canActivate: [authGuard],
      },
      {
        path: 'locations',
        loadComponent: () => import('./pages/locations/locations.page').then((m) => m.LocationsPage),
      },
      {
        path: 'locations/:city/:brand/:id',
        loadComponent: () => import('./pages/station-landing/station-landing.page').then((m) => m.StationLandingPage),
      },
      {
        path: 'roadsurfer',
        loadComponent: () => import('./pages/roadsurfer/roadsurfer.page').then((m) => m.RoadsurferPage),
      },
      {
        path: 'avis-autovermietung',
        data: { partnerKey: 'avis' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'vw-autovermietung',
        data: { partnerKey: 'vw' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'carlundcarla',
        data: { partnerKey: 'carlundcarla' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'bernd',
        data: { partnerKey: 'bernd' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'adac',
        data: { partnerKey: 'adac' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'europcar',
        data: { partnerKey: 'europcar' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'buchbinder',
        data: { partnerKey: 'buchbinder' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'indiecampers',
        data: { partnerKey: 'indiecampers' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'sixt',
        data: { partnerKey: 'sixt' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'wheego',
        data: { partnerKey: 'wheego' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'carvia',
        data: { partnerKey: 'carvia' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'vanever',
        data: { partnerKey: 'vanever' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'myvanture',
        data: { partnerKey: 'myvanture' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'ufodrive',
        data: { partnerKey: 'ufodrive' },
        loadComponent: () => import('./pages/partner-landing/partner-landing.page').then((m) => m.PartnerLandingPage),
      },
      {
        path: 'content/:slug',
        loadComponent: () => import('./pages/cms-content/cms-content.page').then((m) => m.CmsContentPage),
      },
      {
        path: 'camperinfo',
        loadComponent: () => import('./pages/camperinfo/camperinfo.page').then((m) => m.CamperinfoPage),
      },
      {
        path: 'logout',
        loadComponent: () => import('./pages/logout/logout.page').then((m) => m.LogoutPage),
        canActivate: [logoutGuard],
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./pages/forgot-password/forgot-password.page').then((m) => m.ForgotPasswordPage),
      },
      {
        path: 'reset-password/:token',
        loadComponent: () => import('./pages/reset-password/reset-password.page').then((m) => m.ResetPasswordPage),
      },
      {
        path: 'registration',
        loadComponent: () => import('./pages/registration/registration.page').then((m) => m.RegistrationPage),
      },
      {
        path: 'complete-registration/:token',
        canActivate: [registrationCompletionGuard],
        loadChildren: () => [],
      },
      {
        path: 'expired-confirmation/:token',
        loadComponent: () =>
          import('./pages/expired-confirmation/expired-confirmation.page').then((m) => m.ExpiredConfirmationPage),
      },
      {
        path: 'error',
        loadComponent: () => import('./pages/error/error.page').then((m) => m.ErrorPage),
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: '**',
        redirectTo: 'home',
      },
    ],
  },
];
